// PricingPlans.jsx - Planes de Suscripción con opciones de pago
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { initCulqi, configureCulqi, openCulqiCheckout, SUBSCRIPTION_PLANS } from '../services/culqi';
import supabase from '../supabaseClientFixed';

const PricingPlans = ({ currentPlan = 'free', onSelectPlan }) => {
  const [selectedPayment, setSelectedPayment] = useState('card'); // 'card', 'yape', 'plin'
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlanForPurchase, setSelectedPlanForPurchase] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Inicializar Culqi SDK
    initCulqi();
    
    // Configurar Culqi cuando el SDK esté listo
    const checkCulqi = setInterval(() => {
      if (window.Culqi) {
        configureCulqi();
        clearInterval(checkCulqi);
      }
    }, 100);

    // Obtener usuario actual
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    getUser();

    // Escuchar cambios de autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null);
    });

    return () => {
      clearInterval(checkCulqi);
      subscription?.unsubscribe();
    };
  }, []);

  const plans = [
    {
      id: 'trial',
      name: 'Trial Gratis',
      price: 0,
      currency: '',
      period: '3 días',
      color: 'gray',
      features: [
        '3 días de acceso gratuito',
        'Máximo 20 preguntas por examen',
        'Acceso a todas las especialidades',
        'Estadísticas básicas',
        'Modo práctica',
        'Banco de errores básico'
      ],
      limitations: [
        'Sin simulacros ENARM completos',
        'Sin descarga de exámenes',
        'Sin acceso a nuevas funciones'
      ]
    },
    {
      id: 'pro',
      name: 'Plan PRO Mensual',
      price: 39.90,
      currency: 'S/',
      period: '/mes',
      color: 'blue',
      popular: true,
      features: [
        '✨ PREGUNTAS ILIMITADAS',
        '🎯 SIMULACROS ENARM COMPLETOS',
        'Todos los modos de estudio',
        'Explicaciones detalladas',
        'Estadísticas de rendimiento',
        'Historial completo',
        'Acceso a nuevas funciones prioritarias'
      ],
      limitations: []
    },
    {
      id: 'pro_trimestral',
      name: 'Plan PRO Trimestral',
      price: 79.90,
      currency: 'S/',
      period: '/3 meses',
      color: 'purple',
      badge: 'AHORRA 33%',
      features: [
        '✨ PREGUNTAS ILIMITADAS',
        '🎯 SIMULACROS ENARM COMPLETOS',
        'Todos los modos de estudio',
        'Explicaciones detalladas',
        'Estadísticas de rendimiento',
        'Historial completo',
        'Acceso a nuevas funciones prioritarias',
        '💰 Paga 2 meses, obtén 3'
      ],
      limitations: []
    }
  ];

  const paymentMethods = [
    {
      id: 'card',
      name: 'Tarjeta de Crédito/Débito',
      icon: '💳',
      processor: 'stripe',
      description: 'Visa, Mastercard, Amex',
      available: true
    },
    {
      id: 'yape',
      name: 'Yape',
      icon: '📱',
      processor: 'culqi',
      description: 'Pago instantáneo con Yape',
      available: true,
      popular: true
    },
    {
      id: 'plin',
      name: 'Plin',
      icon: '🏦',
      processor: 'culqi',
      description: 'Pago con Plin',
      available: true
    },
    {
      id: 'mercadopago',
      name: 'Mercado Pago',
      icon: '🛒',
      processor: 'mercadopago',
      description: 'Múltiples medios de pago',
      available: true
    }
  ];

  const handleSelectPlan = (plan) => {
    if (plan.id === 'free') {
      onSelectPlan(plan);
    } else {
      setSelectedPlanForPurchase(plan);
      setShowPaymentModal(true);
    }
  };

  const handlePayment = async () => {
    if (!user) {
      alert('Por favor inicia sesión primero');
      setShowPaymentModal(false);
      // Aquí podrías abrir el modal de login
      return;
    }

    setLoading(true);
    const paymentMethod = paymentMethods.find(m => m.id === selectedPayment);
    
    try {
      if (selectedPayment === 'card' || selectedPayment === 'yape' || selectedPayment === 'plin') {
        // Usar Culqi Checkout
        const culqiPlan = selectedPlanForPurchase.id === 'basic' 
          ? SUBSCRIPTION_PLANS.basic 
          : SUBSCRIPTION_PLANS.premium;
        
        // Configurar callback para cuando el pago sea exitoso
        window.culqi = async () => {
          if (window.Culqi.token) {
            // Enviar token al backend para procesar el pago
            const response = await fetch('/.netlify/functions/process-payment', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                token: window.Culqi.token.id,
                email: user.email,
                plan: selectedPlanForPurchase.id,
                amount: selectedPlanForPurchase.price * 100,
                userId: user.id
              })
            });

            const result = await response.json();
            
            if (result.success) {
              alert('¡Pago procesado con éxito! Tu suscripción está activa.');
              onSelectPlan(selectedPlanForPurchase);
              setShowPaymentModal(false);
            } else {
              alert('Error procesando el pago: ' + result.message);
            }
          } else if (window.Culqi.error) {
            console.error('Error de Culqi:', window.Culqi.error);
            alert('Error en el pago: ' + window.Culqi.error.user_message);
          }
        };
        
        // Abrir Culqi Checkout
        openCulqiCheckout(culqiPlan, user.email);
        setShowPaymentModal(false);
      } else if (selectedPayment === 'mercadopago') {
        // TODO: Implementar MercadoPago
        alert('MercadoPago estará disponible próximamente');
      }
    } catch (error) {
      console.error('Error procesando pago:', error);
      alert('Error al procesar el pago. Por favor intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Elige tu plan de estudio
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            Prepárate con la mejor plataforma para tu examen ENARM
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <motion.div
              key={plan.id}
              whileHover={{ scale: 1.02 }}
              className={`relative rounded-2xl p-8 ${
                plan.popular 
                  ? 'bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-500' 
                  : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Más Popular
                  </span>
                </div>
              )}

              {/* Plan Header */}
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {plan.name}
                </h3>
                <div className="flex items-baseline">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">
                    {plan.currency}{plan.price}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400 ml-1">
                    {plan.period}
                  </span>
                </div>
              </div>

              {/* Features */}
              <div className="mb-8">
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700 dark:text-gray-300 text-sm">
                        {feature}
                      </span>
                    </li>
                  ))}
                  {plan.limitations.map((limitation, index) => (
                    <li key={`limit-${index}`} className="flex items-start">
                      <svg className="w-5 h-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-500 dark:text-gray-400 text-sm line-through">
                        {limitation}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA Button */}
              <button
                onClick={() => handleSelectPlan(plan)}
                className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                  currentPlan === plan.id
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : plan.popular
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-800 text-white hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600'
                }`}
                disabled={currentPlan === plan.id}
              >
                {currentPlan === plan.id ? 'Plan Actual' : `Elegir ${plan.name}`}
              </button>
            </motion.div>
          ))}
        </div>

        {/* Payment Methods Section */}
        <div className="mt-16 text-center">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            Métodos de pago aceptados
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <span className="text-2xl">{method.icon}</span>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {method.name}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {method.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Modal */}
        {showPaymentModal && selectedPlanForPurchase && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6"
            >
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Confirmar suscripción
              </h3>
              
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                  Plan seleccionado:
                </p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {selectedPlanForPurchase.name} - {selectedPlanForPurchase.currency}{selectedPlanForPurchase.price}{selectedPlanForPurchase.period}
                </p>
              </div>

              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Selecciona método de pago:
                </p>
                <div className="space-y-2">
                  {paymentMethods.filter(m => m.available).map((method) => (
                    <label
                      key={method.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedPayment === method.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment"
                        value={method.id}
                        checked={selectedPayment === method.id}
                        onChange={(e) => setSelectedPayment(e.target.value)}
                        className="text-blue-600"
                      />
                      <span className="text-2xl">{method.icon}</span>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {method.name}
                          {method.popular && (
                            <span className="ml-2 text-xs bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 px-2 py-0.5 rounded-full">
                              Popular
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {method.description}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  onClick={handlePayment}
                  disabled={loading}
                  className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Procesando...' : 'Procesar Pago'}
                </button>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 text-center">
                Pago seguro procesado por {paymentMethods.find(m => m.id === selectedPayment)?.processor}
              </p>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingPlans;